// g3-telemetry capture — pure browser ESM, no bare-specifier imports, nothing
// touches `window` at import time. This file runs inside a client's own app,
// beside that app's users and their sessions: it must never throw into the
// host page (every wrapper calls the original and rethrows the original
// error, every capture step is try/catch-wrapped and best-effort) and must
// do nothing at all when telemetry is disabled (widget.js's job, not this
// file's — this file only builds the primitives).
//
// Task 2 (tracer) built the real path a report actually needs: trace-id
// propagation over fetch, console/navigation buffers, the replay ring and
// the multipart submit. Task 3 (TDD) added XHR tracing, window error
// listeners, capped errors, and the header/body privacy + 12 MiB replay-trim
// edge cases — see capture.test.ts for the full behavior spec.

export const TRACE_HEADER = "x-g3-trace";

export const LIMITS = {
  console: 200,
  network: 200,
  navigation: 100,
  errors: 50,
  messageChars: 2000,
  bodyChars: 10240,
  replaySegmentMs: 120000,
  maxReplayGzipBytes: 12 * 1024 * 1024,
};

// crypto.randomUUID exists only in a secure context. A page served over plain
// http has none, and a throw here would break the host page's own request.
function newTraceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function createBuffers() {
  return { console: [], network: [], navigation: [], errors: [] };
}

// Push then drop from the front past the cap — oldest evicted first.
export function pushCapped(list, entry, max) {
  list.push(entry);
  while (list.length > max) list.shift();
  return list;
}

export function shouldTrace(url, pageOrigin, traceOrigins) {
  let origin;
  try {
    origin = new URL(url, pageOrigin).origin;
  } catch {
    return false;
  }
  if (origin === pageOrigin) return true;
  return Array.isArray(traceOrigins) && traceOrigins.includes(origin);
}

function safeString(value, cap) {
  if (value == null) return null;
  const s = typeof value === "string" ? value : String(value);
  return s.length > cap ? s.slice(0, cap) : s;
}

// Replaces target.fetch. Returns the ORIGINAL fetch (unwrapped) so callers
// (widget.js's own report submit) can bypass the wrapper — the report POST
// itself must never be recorded in the network buffer.
export function wrapFetch(target, opts) {
  const { buffers, pageOrigin, traceOrigins = [], now = () => Date.now() } = opts;
  const originalFetch = target.fetch.bind(target);

  target.fetch = function g3WrappedFetch(input, init) {
    let url;
    let method = "GET";
    try {
      url = input instanceof Request ? input.url : String(input);
      method = (input instanceof Request ? input.method : init && init.method) || "GET";
    } catch {
      url = String(input);
    }

    const traced = shouldTrace(url, pageOrigin, traceOrigins);
    let traceId = null;
    let finalInput = input;
    let finalInit = init;

    if (traced) {
      try {
        const existingHeaders = new Headers(
          input instanceof Request ? input.headers : (init && init.headers) || undefined,
        );
        traceId = existingHeaders.get(TRACE_HEADER);
        if (!traceId) {
          traceId = newTraceId();
          existingHeaders.set(TRACE_HEADER, traceId);
        }
        if (input instanceof Request) {
          finalInput = new Request(input, { headers: existingHeaders });
        } else {
          finalInit = { ...(init || {}), headers: existingHeaders };
        }
      } catch {
        // best-effort only — never block the real request over a header bug
      }
    }

    const start = now();
    const entry = {
      ts: start,
      trace_id: traceId,
      method,
      url,
      status: null,
      duration_ms: null,
      error: null,
    };

    return originalFetch(finalInput, finalInit).then(
      (res) => {
        entry.status = res.status;
        entry.duration_ms = now() - start;
        pushCapped(buffers.network, entry, LIMITS.network);
        return res;
      },
      (err) => {
        entry.error = safeString(err && err.message ? err.message : err, LIMITS.bodyChars);
        entry.duration_ms = now() - start;
        pushCapped(buffers.network, entry, LIMITS.network);
        throw err;
      },
    );
  };

  return originalFetch;
}

export function wrapConsole(consoleObj, buffers, now = () => Date.now()) {
  const levels = ["log", "info", "warn", "error", "debug"];
  for (const level of levels) {
    if (typeof consoleObj[level] !== "function") continue;
    const original = consoleObj[level].bind(consoleObj);
    consoleObj[level] = function g3WrappedConsole(...args) {
      try {
        const message = args
          .map((a) => {
            if (typeof a === "string") return a;
            try {
              return JSON.stringify(a);
            } catch {
              return String(a);
            }
          })
          .join(" ");
        pushCapped(
          buffers.console,
          { ts: now(), level, message: safeString(message, LIMITS.messageChars) },
          LIMITS.console,
        );
      } catch {
        // never let capture break the app's own logging
      }
      return original(...args);
    };
  }
}

export function watchNavigation(win, buffers, now = () => Date.now()) {
  const push = (kind) => {
    try {
      pushCapped(buffers.navigation, { ts: now(), url: win.location.href, kind }, LIMITS.navigation);
    } catch {
      // best-effort
    }
  };

  push("load");

  try {
    const originalPushState = win.history.pushState.bind(win.history);
    win.history.pushState = function g3WrappedPushState(...args) {
      const result = originalPushState(...args);
      push("pushState");
      return result;
    };
    const originalReplaceState = win.history.replaceState.bind(win.history);
    win.history.replaceState = function g3WrappedReplaceState(...args) {
      const result = originalReplaceState(...args);
      push("replaceState");
      return result;
    };
  } catch {
    // best-effort — history may already be non-configurable in a fake target
  }

  try {
    win.addEventListener("popstate", () => push("popstate"));
    win.addEventListener("hashchange", () => push("hashchange"));
  } catch {
    // best-effort
  }
}

const STRIPPED_HEADERS = new Set(["authorization", "cookie", "set-cookie"]);

// Patches the XMLHttpRequest class's prototype (open/setRequestHeader/send)
// so every instance created after this call is traced. The trace header
// itself is set via the ORIGINAL (unwrapped) setRequestHeader so it never
// pollutes the captured request_headers object — callers only see what
// their own app code explicitly set. `Authorization`/cookie headers are
// still sent (never blocked) but never stored (T-06-09).
export function wrapXhr(XhrClass, opts) {
  const { buffers, pageOrigin, traceOrigins = [], now = () => Date.now() } = opts;
  const proto = XhrClass.prototype;
  const originalOpen = proto.open;
  const originalSetRequestHeader = proto.setRequestHeader;
  const originalSend = proto.send;

  proto.open = function g3WrappedOpen(method, url, ...rest) {
    this.__g3 = { method, url, headers: {}, traced: shouldTrace(url, pageOrigin, traceOrigins) };
    return originalOpen.call(this, method, url, ...rest);
  };

  proto.setRequestHeader = function g3WrappedSetRequestHeader(name, value) {
    if (this.__g3) {
      const lower = String(name).toLowerCase();
      if (!STRIPPED_HEADERS.has(lower)) this.__g3.headers[lower] = safeString(value, LIMITS.bodyChars);
    }
    return originalSetRequestHeader.call(this, name, value);
  };

  proto.send = function g3WrappedSend(body) {
    const state = this.__g3;

    if (state && state.traced) {
      // WR-01: if the host app already called setRequestHeader(TRACE_HEADER, ...)
      // itself, reuse that value instead of setting it again — XHR appends
      // (not replaces) a second setRequestHeader call for the same header
      // name, which would send "existing, generated-uuid" and break
      // correlation both server-side (logger.js's TRACE_RE rejects the
      // comma) and against what capture.js recorded as state.traceId.
      const existing = state.headers[TRACE_HEADER];
      state.traceId = existing || newTraceId();
      if (!existing) {
        try {
          originalSetRequestHeader.call(this, TRACE_HEADER, state.traceId);
        } catch {
          // best-effort only — never block the real request over a header bug
        }
      }
    }

    if (state) {
      state.start = now();
      state.requestBody =
        typeof body === "string" || (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams)
          ? safeString(String(body), LIMITS.bodyChars)
          : null;

      const onLoadEnd = () => {
        try {
          pushCapped(
            buffers.network,
            {
              ts: state.start,
              trace_id: state.traceId || null,
              method: state.method,
              url: state.url,
              status: this.status,
              duration_ms: now() - state.start,
              request_headers: state.headers,
              request_body: state.requestBody,
              response_body: typeof this.responseText === "string" ? safeString(this.responseText, LIMITS.bodyChars) : null,
              error: this.status === 0 ? "network error" : null,
            },
            LIMITS.network,
          );
        } catch {
          // best-effort
        }
      };
      this.addEventListener("loadend", onLoadEnd, { once: true });
    }

    return originalSend.call(this, body);
  };

  return { open: originalOpen, setRequestHeader: originalSetRequestHeader, send: originalSend };
}

export function watchErrors(win, buffers, now = () => Date.now()) {
  try {
    win.addEventListener("error", (event) => {
      pushCapped(
        buffers.errors,
        {
          ts: now(),
          kind: "error",
          message: safeString(event && event.message, LIMITS.bodyChars),
          stack: safeString(event && event.error && event.error.stack, LIMITS.bodyChars),
        },
        LIMITS.errors,
      );
    });
    win.addEventListener("unhandledrejection", (event) => {
      const reason = event && event.reason;
      pushCapped(
        buffers.errors,
        {
          ts: now(),
          kind: "unhandledrejection",
          message: safeString(reason && reason.message ? reason.message : reason, LIMITS.bodyChars),
          stack: safeString(reason && reason.stack, LIMITS.bodyChars),
        },
        LIMITS.errors,
      );
    });
  } catch {
    // best-effort
  }
}

export function recordError(buffers, kind, error, componentStack, now = () => Date.now()) {
  try {
    pushCapped(
      buffers.errors,
      {
        ts: now(),
        kind,
        message: safeString(error && error.message ? error.message : error, LIMITS.bodyChars),
        stack: safeString(error && error.stack, LIMITS.bodyChars),
        component_stack: componentStack != null ? safeString(componentStack, LIMITS.bodyChars) : null,
      },
      LIMITS.errors,
    );
  } catch {
    // never let error capture itself throw
  }
}

// rrweb 2.1.4's own masking surface (confirmed from the installed package's
// recordOptions type): maskTextSelector masks text nodes at/under any
// element matching the selector (Element.closest semantics); maskAllInputs
// forces maskInputFn to be invoked for every input/textarea/select instead
// of only password fields (rrweb-snapshot's maskInputValue only calls
// maskInputFn when maskInputOptions[type] is true — with maskAllInputs left
// false, that's password-only, which would silently record the real value
// of a data-g3-mask text/email input). maskInputFn then does the actual
// selective masking via isMasked, so non-masked inputs still record their
// real value even though rrweb "considered" masking them.
export function startReplay(record, options = {}) {
  const { checkoutEveryNms = LIMITS.replaySegmentMs, blockSelector = "#g3-telemetry-host" } = options;
  let segments = [[], []]; // [previous full segment, current segment]

  const stop = record({
    emit(event, isCheckout) {
      if (isCheckout) {
        segments = [segments[segments.length - 1], []];
      }
      segments[segments.length - 1].push(event);
    },
    checkoutEveryNms,
    blockSelector,
    maskTextSelector: "[data-g3-mask]",
    maskAllInputs: true,
    maskInputFn(text, element) {
      return isMasked(element) ? MASK_TEXT : text;
    },
    maskTextFn(text, element) {
      return isMasked(element) ? MASK_TEXT : text;
    },
  });

  return {
    events() {
      return [...segments[0], ...segments[1]];
    },
    stop,
  };
}

export function collectEnv(win) {
  const nav = win.navigator || {};
  let timezone = "";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    // best-effort
  }
  return {
    viewport: { width: win.innerWidth, height: win.innerHeight },
    dpr: win.devicePixelRatio || 1,
    user_agent: nav.userAgent || "",
    locale: nav.language || "en",
    timezone,
    timestamp: new Date().toISOString(),
  };
}

export function buildReport(input = {}) {
  const buffers = input.buffers || createBuffers();

  const traceIds = [];
  for (const entry of buffers.network) {
    if (entry && entry.trace_id && !traceIds.includes(entry.trace_id)) traceIds.push(entry.trace_id);
  }

  const user =
    input.user && typeof input.user === "object" ? { id: input.user.id, role: input.user.role } : null;

  return {
    schema: 1,
    text: input.text || "",
    reporter_name: input.reporterName || null,
    page_url: input.pageUrl || "",
    picks: input.picks || [],
    build: input.build || { commit: null, deploy_url: null },
    env: input.env || {},
    user,
    trace_ids: traceIds,
    console: buffers.console,
    network: buffers.network,
    navigation: buffers.navigation,
    errors: buffers.errors,
    replay_dropped: !!input.replayDropped,
    screenshot_failed: !!input.screenshotFailed,
  };
}

// ---------------------------------------------------------------------------
// Element picking (plan 06-05 Task 1): selectorFor/elementSnippet work
// against any object exposing the small DOM subset used here (tagName, id,
// parentElement, children, textContent, outerHTML, getBoundingClientRect,
// ownerDocument.querySelectorAll) — tested against a hand-built fake tree
// with no real DOM.

function escapeIdent(id) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(id);
  return String(id).replace(/([^a-zA-Z0-9_-])/g, "\\$1");
}

function hasUniqueId(el) {
  if (!el || !el.id) return false;
  try {
    const doc = el.ownerDocument;
    if (!doc || typeof doc.querySelectorAll !== "function") return false;
    const matches = doc.querySelectorAll("#" + escapeIdent(el.id));
    return !!matches && matches.length === 1;
  } catch {
    return false;
  }
}

function tagOf(el) {
  return String((el && (el.tagName || el.tag)) || "div").toLowerCase();
}

function nthOfType(el) {
  const parent = el && (el.parentElement || el.parent);
  if (!parent || !Array.isArray(parent.children)) return 1;
  const sameTag = parent.children.filter((c) => tagOf(c) === tagOf(el));
  const idx = sameTag.indexOf(el);
  return idx >= 0 ? idx + 1 : 1;
}

// #<escaped id> when el's own id is unique in its document; otherwise a
// `>`-joined tag:nth-of-type(n) path walking up to the nearest ancestor with
// a unique id (that ancestor contributes `#id`, not its own tag segment) or
// to `body`, capped at 12 segments.
export function selectorFor(el) {
  if (hasUniqueId(el)) return "#" + escapeIdent(el.id);

  const segments = [];
  let cur = el;
  for (let i = 0; i < 12 && cur; i++) {
    if (cur !== el && hasUniqueId(cur)) {
      segments.unshift("#" + escapeIdent(cur.id));
      break;
    }
    segments.unshift(`${tagOf(cur)}:nth-of-type(${nthOfType(cur)})`);
    if (tagOf(cur) === "body") break;
    cur = cur.parentElement || cur.parent || null;
  }
  return segments.join(" > ");
}

export const MASK_TEXT = "•••";
export const NAME_STORAGE_KEY = "g3-reporter-name";

// True for a password input, for any element inside an ancestor carrying
// data-g3-mask, and for the data-g3-mask element itself. Walks
// parentElement/parent manually (not Element.closest) so it works against
// both the real DOM and the hand-built fake tree in capture.test.ts.
export function isMasked(el) {
  if (!el) return false;
  const tag = String(el.tagName || el.tag || "").toLowerCase();
  const type = String(el.type || "").toLowerCase();
  if (tag === "input" && type === "password") return true;
  let cur = el;
  while (cur) {
    if (typeof cur.hasAttribute === "function" && cur.hasAttribute("data-g3-mask")) return true;
    cur = cur.parentElement || cur.parent || null;
  }
  return false;
}

function roundRect(rect) {
  const r = rect || {};
  return {
    x: Math.round(r.x || 0),
    y: Math.round(r.y || 0),
    width: Math.round(r.width || 0),
    height: Math.round(r.height || 0),
  };
}

// { selector, text, html, rect } for a picked element. Masked elements
// (Task 2) get MASK_TEXT for text/html while keeping the real selector/rect
// so the fix agent still knows WHERE the report points, just not WHAT was
// there.
export function elementSnippet(el) {
  const selector = selectorFor(el);
  const rect = roundRect(typeof el.getBoundingClientRect === "function" ? el.getBoundingClientRect() : el.rect);
  const masked = isMasked(el);
  return {
    selector,
    text: masked ? MASK_TEXT : safeString((el.textContent || "").trim(), 500),
    html: masked ? MASK_TEXT : safeString(el.outerHTML || "", 5000),
    rect,
  };
}

export async function gzipJson(value) {
  const json = JSON.stringify(value);
  const stream = new Blob([json]).stream().pipeThrough(new CompressionStream("gzip"));
  return await new Response(stream).blob();
}

// Finds the newest full-snapshot (rrweb type 2) event and trims the array to
// start there. ponytail: single trim point restarting at the newest full
// snapshot, not segment-aware — revisit if a trimmed replay still loses
// useful history in practice.
function trimToNewestFullSnapshot(events) {
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i] && events[i].type === 2) return events.slice(i);
  }
  return events;
}

export async function sendReport(input) {
  const {
    fetch: fetchFn,
    ingestUrl,
    reportToken,
    report,
    screenshot,
    replayEvents,
    maxReplayGzipBytes = LIMITS.maxReplayGzipBytes,
  } = input;

  let replayBlob = null;
  let replayDropped = !!(report && report.replay_dropped);

  if (!replayDropped && Array.isArray(replayEvents) && replayEvents.length > 0) {
    try {
      let events = replayEvents;
      let blob = await gzipJson(events);
      if (blob.size > maxReplayGzipBytes) {
        events = trimToNewestFullSnapshot(events);
        blob = await gzipJson(events);
        if (blob.size > maxReplayGzipBytes) {
          blob = null;
          replayDropped = true;
        }
      }
      replayBlob = blob;
    } catch {
      replayBlob = null;
      replayDropped = true;
    }
  }

  async function post(includeReplay, trimmed) {
    const reportJson = { ...report, replay_dropped: includeReplay ? replayDropped : true };
    if (trimmed) {
      // WR-A: a byte-for-character (non-ASCII) blowup can still leave the
      // replay-less body over MAX_BODY_BYTES — network/console are the two
      // biggest, most duplicative buffers, so drop them as a last resort
      // rather than let an honest report fail forever on a dead-end retry.
      reportJson.network = [];
      reportJson.console = [];
      reportJson.buffers_dropped = true;
    }
    const form = new FormData();
    form.set("report", JSON.stringify(reportJson));
    if (screenshot) form.set("screenshot", screenshot, "screenshot.png");
    if (includeReplay && replayBlob) form.set("replay", replayBlob, "replay.json.gz");
    return fetchFn(ingestUrl, {
      method: "POST",
      headers: { Authorization: `Bearer ${reportToken}` },
      body: form,
    });
  }

  try {
    let includeReplay = !!replayBlob;
    let res = await post(includeReplay, false);

    if (res.status === 413 && includeReplay) {
      includeReplay = false;
      res = await post(false, false);
    }

    if (res.status === 413) {
      res = await post(false, true);
    }

    if (res.status >= 200 && res.status < 300) {
      let id;
      try {
        const json = await res.json();
        id = json && json.id;
      } catch {
        // no JSON body — id stays undefined
      }
      return { status: "sent", id };
    }
    if (res.status === 429) return { status: "rate-limited" };
    return { status: "failed" };
  } catch {
    return { status: "failed" };
  }
}

export function startCapture({ win, record, traceOrigins = [], now = () => Date.now() }) {
  const buffers = createBuffers();
  const pageOrigin = win.location.origin;

  const originalFetch = wrapFetch(win, { buffers, pageOrigin, traceOrigins, now });
  if (win.XMLHttpRequest) wrapXhr(win.XMLHttpRequest, { buffers, pageOrigin, traceOrigins, now });
  wrapConsole(win.console, buffers, now);
  watchNavigation(win, buffers, now);
  watchErrors(win, buffers, now);
  const replay = startReplay(record, {});

  return {
    buffers,
    replay,
    originalFetch,
    recordError(error, componentStack) {
      recordError(buffers, "react", error, componentStack, now);
    },
  };
}
